import "./styles.scss";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MainCard from "../../components/MainCard/MainCard";
import Table from "../../components/Table/Table";
import { IRowMyClientsReports, IClientCSVData, IMyClientsParams, IOption } from "../../constants/interfaces";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { RemoveRedEyeOutlined } from "@mui/icons-material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { ClientsStore } from "../../store/MyClientsStore";
import { ROUTE_PATHS } from "../../constants/routePaths";
import { Column, columnTypes, TableOptions } from "../../components/DataTable/datatable.models";
import { Box } from "@mui/material";
import { getSnapshot, Instance } from "mobx-state-tree";
import { ContextModel } from "../../models/Common";
import { DateTime } from "luxon";
import IClientReports from "../../assets/svg/IClientReports";

const UpcomingBirthdays = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const prevParams = location.state && location.state.keepParams ? getSnapshot(ClientsStore.params) : undefined;
  const pageWasRendered = useRef(false);
  const [loadingTable, setLoadingTable] = useState<boolean>(true);
  const [param, setParam] = useState<IMyClientsParams>({
    startDate: prevParams ? prevParams.startDate : undefined,
    endDate: prevParams ? prevParams.endDate : undefined,
    activeProgram: prevParams ? prevParams.activeProgram : undefined,
  });
  const [rows, setRows] = useState<IRowMyClientsReports[] | []>([]);
  const [context, setContext] = useState<Instance<typeof ContextModel> | null>();
  const [groupedOptions, setGroupedOptions] = useState<
    {
      group: string;
      id?: number;
      label: string;
      value: string;
    }[]
  >([]);
  const [defaultGroupedOptions, setDefaultGroupedOptions] = useState<any>();

  const autocompleteStatus: IOption[] = [
    { label: "Active", value: "active" },
    { label: "Cancelled", value: "cancelled" },
  ];
  const isVIPClient: boolean = false;

  /**
   * columns object to be passed to the Table component with the settings for each one.
   */
  const columns: Column[] = [
    {
      name: "Full Name",
      value: "fullName",
      sortable: false,
      mobile: true,
    },
    {
      name: "Birth Date",
      value: "dateOfBirth",
      type: columnTypes.DATE,
      sortable: false,
      mobile: true,
    },
    {
      name: "Active Program",
      value: "activeProgram",
      sortable: false,
      mobile: false,
    },
    {
      name: "Program Start Date",
      value: "programStartDate",
      sortable: false,
      mobile: false,
    },
    {
      name: "Email",
      value: "email",
      sortable: false,
      mobile: false,
    },
    {
      name: "Phone",
      value: "phone",
      sortable: false,
      mobile: false,
    },
    {
      name: "",
      value: "",
      type: columnTypes.CUSTOM,
      renderCell: () => <RemoveRedEyeOutlined sx={{ fontSize: "2rem" }} className="icon-column" />,
      mobile: true,
    },
  ];

  const removeKeys = (keysToRemove: Array<keyof IMyClientsParams>) => {
    const newObj = { ...param, status: "active" };

    keysToRemove.forEach((key) => {
      delete newObj[key];
    });

    return newObj;
  };

  const getGroupedOptions = (dataFilter: IOption[]) => {
    const options: IOption[] = [...autocompleteStatus, ...dataFilter];

    const groupByStatus = options.reduce((groups, option) => {
      if (option.value !== "active" && option.value !== "cancelled") {
        groups["Active Programs"] = groups["Active Programs"] || [];
        groups["Active Programs"].push(option);
      }
      return groups;
    }, {} as Record<string, IOption[]>);
    const groupedOptions = Object.entries(groupByStatus).flatMap(([group, items]) =>
      items.map((item) => ({ ...item, group }))
    );
    setGroupedOptions(groupedOptions);
    if (prevParams?.activeProgram) {
      const selectedAP = (prevParams.activeProgram as number[]).map((ap) => {
        return groupedOptions.find((item) => item.id === ap);
      });
      setDefaultGroupedOptions(selectedAP);
    }
  };

  const getFilters = (options: any) => {
    let newActivePrograms: number[] | undefined = [];

    options.forEach((option: any) => {
      if (option.group === "Active Programs") {
        const programValue = option.id;
        if (newActivePrograms && programValue && !newActivePrograms.includes(programValue)) {
          newActivePrograms.push(programValue);
        }
      }
    });

    setParam((prevParam: any) => ({
      ...prevParam,
      activeProgram: newActivePrograms?.length === 0 ? undefined : newActivePrograms,
      VIP: isVIPClient,
    }));
  };

  const getActivePrograms = async () => {
    try {
      await ClientsStore.fetchActivePrograms(
        removeKeys([
          "page",
          "size",
          "fullName",
          "orderBy",
          "orderByDesc",
          "activeProgram",
          "status",
          "startDate",
          "endDate",
        ]) as IMyClientsParams
      );
      const dataFilter: IOption[] = ClientsStore.activePrograms.map((program) => ({
        id: program.id,
        label: program.name,
        value: program.value,
      }));

      getGroupedOptions(dataFilter);
    } catch (error) {
      console.log(error);
    }
  };

  const onRowClicked = (row: object) => {
    navigate(`${ROUTE_PATHS.MY_CLIENTS_TABS.replace(":id", (row as IRowMyClientsReports)?.id.toString())}`, {
      state: { backLinkText: "Client Report", backLinkUrl: ROUTE_PATHS.UPCOMING_BIRTHDAYS },
    });
  };

  const getDate = (startDate: string | undefined, endDate: string | undefined) => {
    setParam({ ...param, startDate, endDate });
  };

  const getDataForCSV = async (): Promise<IClientCSVData[]> => {
    const paramToCSV = { ...param, page: undefined, size: undefined, withoutPagination: true };
    const data = await ClientsStore.fetchClientReports(paramToCSV, "birthday", true);
    return data ?? [];
  };

  const onTableOptionsChanged = (tableParams: TableOptions) => {
    //save table pagination
    setParam({
      ...param,
      page: tableParams.page,
      size: tableParams.pageSize,
    });
  };

  const getUpcomingBirthdays = async () => {
    try {
      const result = await ClientsStore.fetchBirthdayReport(param);
      setRows(result.items);
      setContext(result.context);
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  useEffect(() => {
    ClientsStore.clear();
    const fetchData = async () => {
      setLoadingTable(true);
      await getActivePrograms();
    };
    fetchData();
    pageWasRendered.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pageWasRendered.current) {
      const fetchData = async () => {
        setLoadingTable(true);
        try {
          await getUpcomingBirthdays();
        } catch (error) {
          console.log(error);
        } finally {
          setLoadingTable(false);
        }
      };
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [param]);

  useEffect(() => {
    pageWasRendered.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MainCard>
      <Table
        getCSVData={getDataForCSV}
        idTable="birthday-table"
        header={
          <Box
            sx={{
              padding: 1.25,
              fontSize: "1.4rem",
              textAlign: "left",
              marginBottom: 0.5,
              borderBottom: 2,
              borderColor: "divider",
            }}
          >
            <span style={{ fontWeight: "bold", fontSize: "16px" }}>Upcoming Birthdays:</span>
            {" Send a birthday note! Change the date range to show more clients."}
          </Box>
        }
        tableProps={{
          columns: columns,
          data: rows,
          loading: loadingTable,
          totalRows: context?.totalRows ?? 0,
          handleTableOptionsChanged: onTableOptionsChanged,
          handleRowClicked: onRowClicked,
          expandRowOnMobileBreakpoint: true,
          mobileBreakpoint: 900,
          paginationValue:
            prevParams && prevParams.page && prevParams.size
              ? {
                  page: prevParams.page - 1,
                  size: prevParams.size,
                }
              : undefined,
        }}
        calendarProps={{
          withCalendarRange: true,
          sx: { maxWidth: "18rem" },
          customOnChange: getDate,
          allowFuture: true,
          startDate: prevParams?.startDate ? DateTime.fromISO(prevParams.startDate) : undefined,
          endDate: prevParams?.endDate ? DateTime.fromISO(prevParams.endDate) : undefined,
        }}
        autocompleteFilterProps={{
          multiValue: defaultGroupedOptions,
          withInputAutocompleteMulti: true,
          iconMulti: FilterAltIcon,
          optionsMulti: groupedOptions,
          labelMulti: "Filter List",
          placeholderMulti: "Filter List",
          sxMulti: { width: "30rem" },
          OnChangeMulti: getFilters,
          idMulti: "clients-input-filter",
        }}
        downloadCSVButtonProps={{
          id: "client-btn-dowload",
          show: true,
          text: "Download",
          disabled: rows?.length === 0 ? true : false,
          startIcon: <SaveAltIcon />,
          variant: "outlined",
          size: "large",
        }}
        buttonsProps={[
          {
            id: "client-btn-reports",
            show: true,
            text: "Client Reports",
            startIcon: <IClientReports width="2rem" height="2rem" />,
            variant: "outlined",
            size: "large",
            className: "five-inputs",
            onClick: () => {
              navigate(`${ROUTE_PATHS.CLIENT_REPORTS}`);
            },
          },
        ]}
      />
    </MainCard>
  );
};

export default UpcomingBirthdays;
